import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AlertTriangle, Stethoscope, ShieldCheck, X } from "lucide-react";
import { useAuth, API } from "../App";

const PRIMARY = "#00658D";
const ACCENT = "#00AEEF";
const btnGradient = `linear-gradient(180deg, ${ACCENT} 0%, ${PRIMARY} 100%)`;

/**
 * Patient-only disclaimer gate.
 * Shown immediately after login on the patient dashboard.
 * - "I agree"   → onAccept()  → user continues into the dashboard
 * - "I disagree" → onDecline() → caller should log the user out and redirect to "/"
 */
export default function DisclaimerGate({ onAccept, onDecline }) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);
  const sections = t("disclaimer.sections", { returnObjects: true }) || [];

  const handleAcceptClick = async () => {
    if (submitting) return;
    setSubmitting(true);
    // GDPR Art. 7 — record explicit consent server-side before continuing.
    // Failure is non-fatal: the user must not be locked out of the dashboard
    // if the audit log is temporarily unreachable — the frontend UI still
    // guarantees the button was clicked.
    try {
      if (token) {
        await axios.post(
          `${API}/user/consent`,
          { consent_type: "medical_disclaimer", granted: true, source: "disclaimer-gate" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (e) {
      console.warn("Consent logging failed (non-blocking):", (e && e.response && e.response.data) || (e && e.message));
    } finally {
      setSubmitting(false);
      onAccept();
    }
  };

  // Track if the user has scrolled the disclaimer body to the bottom
  // (UX nicety — agreement button is not blocked, but a hint is shown until they read).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining < 20) setScrolledToEnd(true);
    };
    onScroll(); // run once for short viewports
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // Lock background scroll while modal open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center p-4 md:p-6 overflow-y-auto"
      style={{ background: "rgba(12,45,74,0.65)", backdropFilter: "blur(6px)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="disclaimer-gate-title"
      data-testid="disclaimer-gate"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-4 md:my-8 max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)] flex flex-col animate-in zoom-in-95 fade-in duration-200"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b" style={{ borderColor: "rgba(189,200,209,0.4)" }}>
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: "#fee2e2" }}
            >
              <Stethoscope className="w-5 h-5" style={{ color: "#b91c1c" }} />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: "#b91c1c" }}>
                {t("disclaimer.gate.gateTitle")}
              </p>
              <h2
                id="disclaimer-gate-title"
                className="text-xl md:text-2xl font-extrabold tracking-tight"
                style={{ color: "#0c2d4a" }}
                data-testid="disclaimer-gate-title"
              >
                {t("disclaimer.title")}
              </h2>
              <p className="text-sm mt-1" style={{ color: "#6e7881" }}>
                {t("disclaimer.gate.gateSubtitle")}
              </p>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-testid="disclaimer-gate-body">
          {/* Emergency banner */}
          <div
            className="rounded-xl p-4 flex items-start gap-3"
            style={{
              background: "linear-gradient(180deg, #fff5f5 0%, #ffeaea 100%)",
              border: "1px solid #f5b5b5",
            }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: "#dc2626" }}
            >
              <AlertTriangle className="w-4 h-4 text-white" />
            </div>
            <p className="text-sm font-semibold leading-relaxed" style={{ color: "#7f1d1d" }}>
              {t("disclaimer.emergencyBanner")}
            </p>
          </div>

          {/* Intro */}
          <p className="text-sm leading-relaxed" style={{ color: "#3e4850" }} data-testid="disclaimer-gate-intro">
            {t("disclaimer.intro")}
          </p>

          {/* Sections */}
          {Array.isArray(sections) &&
            sections.map((sec, idx) => (
              <div
                key={`gate-section-${idx}`}
                className="rounded-xl p-4 border"
                style={{ borderColor: "rgba(189,200,209,0.4)", background: "#fafbfd" }}
                data-testid={`disclaimer-gate-section-${idx}`}
              >
                <h3 className="font-bold text-sm mb-1.5" style={{ color: "#0c2d4a" }}>
                  {sec.heading}
                </h3>
                <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: "#3e4850" }}>
                  {sec.body}
                </p>
              </div>
            ))}
        </div>

        {/* Footer with actions */}
        <div
          className="px-6 py-4 border-t flex flex-col gap-3"
          style={{ borderColor: "rgba(189,200,209,0.4)", background: "#f8fafc" }}
        >
          {!scrolledToEnd && (
            <p
              className="text-xs text-center italic"
              style={{ color: "#6e7881" }}
              data-testid="disclaimer-gate-scroll-hint"
            >
              ↓ {t("disclaimer.gate.gateSubtitle")}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleAcceptClick}
              disabled={submitting}
              className="flex-1 px-5 py-3 rounded-full font-bold text-white text-sm transition-all hover:opacity-95 active:scale-[0.98] inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ background: btnGradient, boxShadow: "0 6px 18px rgba(0,101,141,0.3)" }}
              data-testid="disclaimer-gate-accept-btn"
            >
              <ShieldCheck className="w-4 h-4" />
              {t("disclaimer.gate.agree")}
            </button>
            <button
              onClick={onDecline}
              className="flex-1 px-5 py-3 rounded-full font-semibold text-sm transition-all hover:bg-slate-100 active:scale-[0.98] inline-flex items-center justify-center gap-2"
              style={{
                color: "#7f1d1d",
                background: "#fff",
                border: "1px solid #f5b5b5",
              }}
              data-testid="disclaimer-gate-decline-btn"
            >
              <X className="w-4 h-4" />
              {t("disclaimer.gate.disagree")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
